import PropTypes from 'prop-types';
import styled from 'styled-components';

import { config } from '_config';
import { theme } from '_constants/theme';

import * as Layout from '_components/atomic/layouts';
import JoinBanner from '../JoinBanner';

export const roleOfRegisteredAgentsInBusinessFormationContents = [
  'What is a Registered Agent?',
  'Responsibilities of a Registered Agent',
  'Benefits of Having a Registered Agent',
  'Choosing the Right Registered Agent',
  'How to Appoint a Registered Agent',
];

const StyledContainer = styled.div`
  position: relative;
`;

const StyledBlock = styled.div`
  position: relative;
  z-index: 1;

  ol,
  ul {
    padding-top: ${theme.spaces.regular}px;

    li {
      margin-bottom: ${theme.spaces.regular}px;

      &::marker {
        font-weight: 700;
      }
    }
  }
`;

const StyledBackgroundImageContainer = styled.div`
  position: absolute;
  top: 40%;
  width: 100%;

  img {
    width: 100%;
  }
`;

const RoleOfRegisteredAgentsInBusinessFormationArticle = ({ idPrefix }) => {
  return (
    <StyledContainer>
      <Layout.Card.Text>
        When forming a business, one critical but often overlooked component is
        the registered agent. A registered agent plays a vital role in ensuring
        that your business complies with state regulations and stays informed of
        legal obligations. This article explores the responsibilities, benefits,
        and importance of having a registered agent for your business.
      </Layout.Card.Text>
      <Layout.Divider />
      <StyledBlock id={`${idPrefix}-${0}`}>
        <Layout.Card.Text
          as="h1"
          size={theme.fontSizes.large}
          color={theme.colors.indigo[600]}
          boldest
          centered
          underline
        >
          What is a Registered Agent?
        </Layout.Card.Text>
        <Layout.Card.Text>
          A registered agent is a designated individual or company authorized to
          receive legal documents, government notices, and official
          correspondence on behalf of your business. Every state requires
          businesses, such as corporations and LLCs, to appoint a registered
          agent as part of the formation process.
        </Layout.Card.Text>
      </StyledBlock>
      <Layout.Divider />
      <StyledBlock id={`${idPrefix}-${1}`}>
        <Layout.Card.Text
          as="h1"
          size={theme.fontSizes.large}
          color={theme.colors.indigo[600]}
          boldest
          centered
          underline
        >
          Responsibilities of a Registered Agent
        </Layout.Card.Text>
        <Layout.Card.Text as="h2" boldest centered noBottomSpace>
          Receiving Legal Documents
        </Layout.Card.Text>
        <Layout.Card.Text>
          The primary responsibility of a registered agent is to receive service
          of process, which includes legal documents such as lawsuits, summons,
          and subpoenas. Ensuring these documents are promptly delivered to the
          business owners is crucial for timely responses and legal compliance.
        </Layout.Card.Text>
        <Layout.Card.Text as="h2" boldest centered noBottomSpace>
          Handling Government Correspondence
        </Layout.Card.Text>
        <Layout.Card.Text>
          Registered agents receive important government notices, including tax
          forms, annual report reminders, and compliance-related documents. They
          ensure that these critical communications reach the appropriate
          business personnel.
        </Layout.Card.Text>
        <Layout.Card.Text as="h2" boldest centered noBottomSpace>
          Maintaining Compliance
        </Layout.Card.Text>
        <Layout.Card.Text>
          By keeping track of filing deadlines and compliance requirements,
          registered agents help businesses stay in good standing with state
          authorities. This includes timely submission of annual reports,
          business licenses, and other mandatory filings.
        </Layout.Card.Text>
      </StyledBlock>
      <Layout.Divider />
      <StyledBlock id={`${idPrefix}-${2}`}>
        <Layout.Card.Text
          as="h1"
          size={theme.fontSizes.large}
          color={theme.colors.indigo[600]}
          boldest
          centered
          underline
        >
          Benefits of Having a Registered Agent
        </Layout.Card.Text>
        <Layout.Card.Text as="h2" boldest centered noBottomSpace>
          Legal Compliance
        </Layout.Card.Text>
        <Layout.Card.Text>
          A registered agent ensures that your business complies with state laws
          by receiving and forwarding important legal and regulatory documents.
          This helps you avoid penalties, fines, and potential legal issues
        </Layout.Card.Text>
        <Layout.Card.Text as="h2" boldest centered noBottomSpace>
          Privacy Protection
        </Layout.Card.Text>
        <Layout.Card.Text>
          Using a registered agent protects your privacy by keeping your
          personal address off public records. This is especially important for
          home-based businesses, as it prevents unwanted solicitations and
          maintains confidentiality.
        </Layout.Card.Text>
        <Layout.Card.Text as="h2" boldest centered noBottomSpace>
          Reliable Point of Contact
        </Layout.Card.Text>
        <Layout.Card.Text>
          Having a registered agent provides a reliable point of contact for
          legal and government correspondence. This ensures that important
          documents are received and addressed promptly, reducing the risk of
          missed deadlines and legal complications.
        </Layout.Card.Text>
        <Layout.Card.Text as="h2" boldest centered noBottomSpace>
          Flexibility
        </Layout.Card.Text>
        <Layout.Card.Text>
          A registered agent allows you to maintain flexible business hours and
          locations. Since the agent must be available during standard business
          hours to receive documents, you are free to manage your business
          without being tied to a specific location or schedule.
        </Layout.Card.Text>
      </StyledBlock>
      <Layout.Divider />
      <StyledBlock id={`${idPrefix}-${3}`}>
        <Layout.Card.Text
          as="h1"
          size={theme.fontSizes.large}
          color={theme.colors.indigo[600]}
          boldest
          centered
          underline
        >
          Choosing the Right Registered Agent
        </Layout.Card.Text>
        <Layout.Card.Text as="h2" boldest centered noBottomSpace>
          Professional Service Providers
        </Layout.Card.Text>
        <Layout.Card.Text>
          Many businesses choose to hire professional registered agent services.
          These providers offer expertise, reliability, and additional benefits
          such as compliance monitoring and document management.
        </Layout.Card.Text>
        <Layout.Card.Text as="h2" boldest centered noBottomSpace>
          Qualifications
        </Layout.Card.Text>
        <Layout.Card.Text>
          When selecting a registered agent, ensure they meet state
          requirements. The agent must have a physical address in the state of
          formation and be available during standard business hours.
        </Layout.Card.Text>
        <Layout.Card.Text as="h2" boldest centered noBottomSpace>
          Reputation and Experience
        </Layout.Card.Text>
        <Layout.Card.Text>
          Consider the reputation and experience of the registered agent
          service. Look for providers with positive reviews, a history of
          reliability, and a track record of helping businesses maintain
          compliance.
        </Layout.Card.Text>
      </StyledBlock>
      <Layout.Divider />
      <StyledBlock id={`${idPrefix}-${4}`}>
        <Layout.Card.Text
          as="h1"
          size={theme.fontSizes.large}
          color={theme.colors.indigo[600]}
          boldest
          centered
          underline
        >
          How to Appoint a Registered Agent
        </Layout.Card.Text>
        <Layout.Card.Text as="h2" boldest centered noBottomSpace>
          During Business Formation
        </Layout.Card.Text>
        <Layout.Card.Text>
          When forming your business, you will designate a registered agent as
          part of the incorporation or LLC formation process. This information
          is included in the formation documents filed with the state.
        </Layout.Card.Text>
        <Layout.Card.Text as="h2" boldest centered noBottomSpace>
          Changing a Registered Agent
        </Layout.Card.Text>
        <Layout.Card.Text>
          f you need to change your registered agent, file the appropriate form
          with your state’s Secretary of State office. This process typically
          involves submitting a change of agent form and paying a small fee.
        </Layout.Card.Text>
      </StyledBlock>
      <Layout.Divider />
      <Layout.Card.Text>
        The role of a registered agent is essential in business formation and
        ongoing compliance. By receiving legal documents, handling government
        correspondence, and maintaining compliance, a registered agent helps
        ensure your business operates smoothly and legally. Choosing a reliable
        registered agent provides peace of mind, privacy protection, and
        flexibility, allowing you to focus on growing your business.
      </Layout.Card.Text>
      <JoinBanner />
      <StyledBackgroundImageContainer>
        <picture>
          <source
            srcSet={`${config.basePath}/images/blog-arrow-logo2.svg`}
            type="image/svg+xml"
          />
          <img
            src={`${config.basePath}/images/blog-arrow-logo2.svg`}
            alt="Background Logo"
          />
        </picture>
      </StyledBackgroundImageContainer>
    </StyledContainer>
  );
};

RoleOfRegisteredAgentsInBusinessFormationArticle.propTypes = {
  idPrefix: PropTypes.string,
};

export default RoleOfRegisteredAgentsInBusinessFormationArticle;
